import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import type {
  Articulo,
  ArticuloBasico,
  ArticulosBasicoResponse,
  ArticulosPaginatedResponse,
  ArticulosQueryParams,
  ArticuloWritePayload,
} from '../models/articulo.model';

const FETCH_ALL_PAGE_SIZE = 100;

@Injectable({ providedIn: 'root' })
export class ArticulosService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/api`;

  private readonly articulosBasicoCaches = new Map<string, Observable<ArticuloBasico[]>>();

  /**
   * Lista completa sin paginar (datos mínimos). GET /api/articulos/basico (caché por `fields`).
   * @param query p. ej. `{ fields: 'config_servicio,periocidad' }` para incluir esos campos en cada ítem.
   */
  getArticulosBasico(query?: { fields?: string }): Observable<ArticuloBasico[]> {
    const cacheKey = query?.fields?.trim() ?? '';
    let cached$ = this.articulosBasicoCaches.get(cacheKey);
    if (!cached$) {
      let params = new HttpParams();
      if (cacheKey) {
        params = params.set('fields', cacheKey);
      }
      cached$ = this.http
        .get<ArticulosBasicoResponse>(`${this.baseUrl}/articulos/basico`, { params })
        .pipe(
          map((res) => res.data ?? []),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
      this.articulosBasicoCaches.set(cacheKey, cached$);
    }
    return cached$;
  }

  /** Invalida todas las variantes cacheadas de artículos básicos (p. ej. tras logout). */
  clearArticulosBasicoCache(): void {
    this.articulosBasicoCaches.clear();
  }

  /**
   * Catálogo completo (todas las páginas) para formularios que necesitan el listado íntegro.
   * GET /api/articulos con paginación Laravel.
   */
  getArticulos(): Observable<Articulo[]> {
    return this.loadArticulosAccumulated(1, []);
  }

  private loadArticulosAccumulated(page: number, acc: Articulo[]): Observable<Articulo[]> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('per_page', String(FETCH_ALL_PAGE_SIZE));
    return this.http.get<ArticulosPaginatedResponse>(`${this.baseUrl}/articulos`, { params }).pipe(
      switchMap((res) => {
        const chunk = res.data ?? [];
        const next = [...acc, ...chunk];
        const lastPage = res.meta?.last_page ?? 1;
        if (page >= lastPage || chunk.length === 0) {
          return of(next);
        }
        return this.loadArticulosAccumulated(page + 1, next);
      }),
    );
  }

  /** Listado paginado (pantalla de configuración, etc.). GET /api/articulos */
  getArticulosPaged(params: ArticulosQueryParams = {}): Observable<ArticulosPaginatedResponse> {
    let httpParams = new HttpParams();
    if (params.page != null) httpParams = httpParams.set('page', String(params.page));
    if (params.per_page != null) httpParams = httpParams.set('per_page', String(params.per_page));
    if (params.search?.trim()) httpParams = httpParams.set('search', params.search.trim());
    if (params.id_familia != null && Number.isFinite(params.id_familia)) {
      httpParams = httpParams.set('id_familia', String(params.id_familia));
    }
    // TODO(API articulos): validar que el backend expone y documenta estos filtros en GET /api/articulos.
    if (params.periocidad?.trim()) {
      httpParams = httpParams.set('periocidad', params.periocidad.trim());
    }
    if (params.cuando_se_factura?.trim()) {
      httpParams = httpParams.set('cuando_se_factura', params.cuando_se_factura.trim());
    }
    if (params.activo === '0' || params.activo === '1') {
      httpParams = httpParams.set('activo', params.activo);
    }
    if (params.is_pack === '0' || params.is_pack === '1') {
      httpParams = httpParams.set('is_pack', params.is_pack);
    }
    if (params.fecha_desde?.trim()) {
      httpParams = httpParams.set('fecha_desde', params.fecha_desde.trim());
    }
    if (params.fecha_hasta?.trim()) {
      httpParams = httpParams.set('fecha_hasta', params.fecha_hasta.trim());
    }
    if (params.sort_by?.trim()) {
      httpParams = httpParams.set('sort_by', params.sort_by.trim());
    }
    if (params.sort_dir === 'asc' || params.sort_dir === 'desc') {
      httpParams = httpParams.set('sort_dir', params.sort_dir);
    }
    return this.http.get<ArticulosPaginatedResponse>(`${this.baseUrl}/articulos`, { params: httpParams });
  }

  /** Detalle por id. GET /api/articulos/:id */
  getArticulo(id: number): Observable<Articulo> {
    return this.http.get<{ data: Articulo } | Articulo>(`${this.baseUrl}/articulos/${id}`).pipe(
      map((res) => (res != null && typeof res === 'object' && 'data' in res && res.data != null ? res.data : (res as Articulo))),
    );
  }

  /** Alta. POST /api/articulos */
  createArticulo(payload: ArticuloWritePayload): Observable<Articulo> {
    return this.http.post<{ data: Articulo } | Articulo>(`${this.baseUrl}/articulos`, payload).pipe(
      map((res) => (res != null && typeof res === 'object' && 'data' in res && res.data != null ? res.data : (res as Articulo))),
    );
  }

  /** Actualización. PUT /api/articulos/:id */
  updateArticulo(id: number, payload: ArticuloWritePayload): Observable<Articulo> {
    return this.http.put<{ data: Articulo } | Articulo>(`${this.baseUrl}/articulos/${id}`, payload).pipe(
      map((res) => (res != null && typeof res === 'object' && 'data' in res && res.data != null ? res.data : (res as Articulo))),
    );
  }

  /** Baja. DELETE /api/articulos/:id */
  deleteArticulo(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/articulos/${id}`);
  }
}
